if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.retries = 0;

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        this.productId = this.querySelector('[name="product-id"]').value;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        this.querySelector('product-media-gallery').classList.remove('product-media-gallery--loaded');
        // Find the nearest section
        const nearestSection = target.closest('section.shopify-section') || target.closest('quick-buy-modal');
        // Add the 'loading' class when the request begins
        if (nearestSection) {
          nearestSection.classList.add('dynamic-content-loading');
          document.dispatchEvent(new CustomEvent('theme:loading:start'), {
            bubbles: true,
          });
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

        if (this.closest('quick-buy-modal')) {
          this.closest('quick-buy-modal').fetchProduct(requestUrl);
          return;
        }

        const callback = shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl);

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: (response) => {
            // Remove the 'dynamic-content-loading' class when the request ends or fails
            if (nearestSection) {
              nearestSection.classList.remove('dynamic-content-loading');
              document.dispatchEvent(new CustomEvent('theme:loading:end'), {
                bubbles: true,
              });
            }

            // Call the original callback
            callback(response);
          },
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            document.dispatchEvent(
              new CustomEvent('product-info:loaded', {
                bubbles: true,
              })
            );
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelectorAll('.product-variant-picker');
        if (variantSelects.length) {
          variantSelects.forEach((variantSelect) => {
            const target = document.querySelector(`[data-variant-option="${variantSelect.dataset.variantOption}"]`);
            HTMLUpdateUtility.viewTransition(target, variantSelect, this.preProcessHtmlCallbacks);
          });
        }
      }

      processAccordions(html, type) {
        // We'll build a convenient selector and use getAttribute for consistency
        const dataAttr = `data-dynamic-variant-accordion-${type}`;

        // All existing accordions in the live DOM that match our data attribute
        const currentAccordions = Array.from(document.querySelectorAll(`[${dataAttr}]`));

        // Figure out which accordions are currently open
        const openAccordions = currentAccordions
          .filter((accordion) => {
            const details = accordion.querySelector('details');
            return details && details.open;
          })
          .map((accordion) => accordion.getAttribute(dataAttr));

        // All accordions in the *newly fetched* HTML snippet
        const newAccordions = Array.from(html.querySelectorAll(`[${dataAttr}]`));

        // 1. Remove any *current* accordions that do not exist in *new* accordions
        currentAccordions.forEach((existing) => {
          const oldKey = existing.getAttribute(dataAttr);
          const stillExists = newAccordions.some((acc) => acc.getAttribute(dataAttr) === oldKey);
          if (!stillExists) {
            existing.remove();
          }
        });

        // 2. Insert/replace each new accordion into the DOM
        let previousSibling = null;

        newAccordions.forEach((newAccordion) => {
          const key = newAccordion.getAttribute(dataAttr);

          // If this accordion was open previously, keep it open
          if (openAccordions.includes(key)) {
            const details = newAccordion.querySelector('details');
            if (details) {
              details.setAttribute('open', '');
              details.classList.add('accordion-details--open');
            }
          }

          // Find if an accordion with the same key is already in the live DOM
          const existingInDOM = document.querySelector(`[${dataAttr}="${key}"]`);

          if (existingInDOM) {
            // Directly replace the old accordion with the new one
            existingInDOM.replaceWith(newAccordion);
            previousSibling = newAccordion;
          } else {
            // If we have a previous sibling, place after it in the DOM
            if (previousSibling) {
              previousSibling.after(newAccordion);
              previousSibling = newAccordion;
            } else {
              // Otherwise, insert after the "accordion-dynamic-content-{type}" marker.
              // If you want to *append* inside a container instead, you can do target.append(newAccordion).
              const target = document.querySelector(`.accordion-dynamic-content-${type}`);
              if (target) {
                target.after(newAccordion);
                previousSibling = newAccordion;
              }
            }
          }
        });
      }

      handleDynamicContent(html) {
        // Update storefront product
        const storefrontProducts = html.querySelectorAll('[is="storefront-product"]');
        storefrontProducts.forEach((element) => {
          const id = element.id;
          if (document.getElementById(id)) {
            const currentProduct = document.getElementById(id);
            const newProduct = element;
            Object.entries(newProduct.dataset).forEach(([key, value]) => {
              if (currentProduct.dataset[key] !== value) {
                currentProduct.dataset[key] = value;
              }
            });
          }
        });

        const dynamicContent = html.querySelectorAll('[data-dynamic-id]');
        dynamicContent.forEach((element) => {
          const liveContent = document.querySelector(`[data-dynamic-id="${element.dataset.dynamicId}"]`);
          if (liveContent) {
            liveContent.innerHTML = element.innerHTML;
            if (liveContent.querySelector('[data-carousel-item]')) {
              liveContent.scrollLeft = 0;
              const swiper = liveContent.closest('css-swiper');
              if (swiper) {
                swiper.removeExternalControlListeners();
                swiper.initExternalControls();
              }
            }
          }
        });

        this.processAccordions(html, 'metafields');
        this.processAccordions(html, 'notifications');
      }

      setFirstAvailableVariant(html) {
        const variantPickers = Array.from(html.querySelectorAll('.product-variant-picker')).reverse();
        variantPickers.forEach((variantPicker) => {
          const selectedOption = Array.from(
            variantPicker.querySelectorAll(
              'select:not(.exclude-first-available-variant) option[selected]:not(.disabled), fieldset input:checked:not(.disabled)'
            )
          )
            .filter((element) => !element.closest('.in-transition'))
            .filter((element) => !element.closest('.external-selects'))
            .map(({ dataset }) => dataset.optionValueId);

          if (!selectedOption.length) {
            const currentVariantPicker = document.getElementById(variantPicker.id);
            const firstOption = variantPicker.querySelector(
              'select:not(.exclude-first-available-variant) option:not(.disabled), fieldset input:not(.disabled)'
            );
            const currentFirstOption = currentVariantPicker.querySelector(
              `[data-option-value-id="${firstOption.dataset.optionValueId}"]`
            );
            if (currentFirstOption) {
              currentFirstOption.click();
            }
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.toggleLoadersByVariantId(variant?.id);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // Toggle again because the html content is updated
          this.toggleLoadersByVariantId(variant?.id);

          if (!variant) {
            if (this.retries < 1) {
              this.retries++;
              this.setFirstAvailableVariant(html);
            } else {
              this.setUnavailable();
            }
            return;
          } else {
            this.retries = 0;
          }

          // Only process dynamic content (including accordions) when we have a valid variant
          this.handleDynamicContent(html);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const priceData = html.querySelector(`#Price-${this.sectionId} .price-item--regular`);
          const price = priceData ? priceData.innerHTML : '';

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Drop Mechanic
          const dropMechanicSource = html.getElementById(`drop-mechanic-${this.sectionId}`);
          const destination = this.querySelector(`#drop-mechanic-${this.dataset.section}`);
          if (dropMechanicSource.children.length && destination.children.length) {
            destination.querySelector('.product__drop-mechanic-progress-bar-title-wrapper').innerHTML =
              dropMechanicSource.querySelector('.product__drop-mechanic-progress-bar-title-wrapper').innerHTML;

            const progressBarSource = dropMechanicSource.querySelector('.product__drop-mechanic-progress-bar');
            const progressBarDestination = destination.querySelector('.product__drop-mechanic-progress-bar');
            if (progressBarSource && !progressBarDestination) {
              destination.append(progressBarSource);
            } else if (!progressBarSource && progressBarDestination) {
              progressBarDestination.remove();
            }
          } else if (dropMechanicSource.children.length && !destination.children.length) {
            destination.innerHTML = dropMechanicSource.innerHTML;
          } else if (!dropMechanicSource.children.length) {
            Array.from(destination.children).forEach((child) => child.remove());
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.handleNotificationButton(html.getElementById(`ProductSubmitButton-${this.sectionId}`));

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            html.getElementById(`ProductSubmitButton-${this.sectionId}`).hasAttribute('disabled') === true
              ? window.variantStrings.soldOut
              : `${window.variantStrings.addToCart} ${price ? `• ${price === 0 ? window.variantStrings.free : price}` : ''}`
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
              product: {
                id: this.productId,
              },
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(variantId) {
        const mediaGallery = this.querySelector('product-media-gallery');
        mediaGallery.updateMediaGallery(variantId);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      toggleLoadersByVariantId(variantId) {
        const loaders = document.querySelectorAll(
          '.product-media-gallery__variant-thumbnail .lazy-image, product-media-gallery .line-loader, .product-variant-picker .line-loader'
        );

        if (!variantId) {
          loaders.forEach((loader) => {
            loader.classList.add('loading');
          });
        } else {
          loaders.forEach((loader) => {
            loader.classList.remove('loading');
          });
        }
      }

      get productForm() {
        return this.querySelector(`product-form.primary__product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
